import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Bot, Loader2, Trash2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const backendUrl = "https://www.sevaafoundation.com/seva-backend";
const defaultImage = "https://www.sevaafoundation.com/og-blogs.jpg";

type BlogItem = {
  id: number;
  title: string;
  slug?: string;
  category?: string;
  short_description?: string;
  cover_image?: string;
  author_name?: string;
  status?: "draft" | "published";
  generation_source?: "manual" | "ai";
  created_at?: string;
  published_at?: string;
};

const BlogList = () => {
  const [blogs, setBlogs] = useState<BlogItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [blogToDelete, setBlogToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem("sevaa_admin_token");

  const fetchBlogs = async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(`${backendUrl}/api/blogs/list.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Blogs could not be loaded.");
      }

      setBlogs(Array.isArray(data.blogs) ? data.blogs : []);
    } catch (fetchError) {
      setError(fetchError instanceof Error ? fetchError.message : "Blogs could not be loaded.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchBlogs();
  }, []);

  const confirmDelete = async () => {
    if (blogToDelete === null) return;

    setIsDeleting(true);

    try {
      const response = await fetch(`${backendUrl}/api/blogs/delete.php?id=${blogToDelete}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Failed to delete blog.");
      }

      setBlogs((previous) => previous.filter((blog) => blog.id !== blogToDelete));
      setBlogToDelete(null);
    } catch (deleteError) {
      alert(deleteError instanceof Error ? deleteError.message : "Something went wrong while deleting.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="relative min-h-screen p-6">
      <AnimatePresence>
        {blogToDelete !== null && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !isDeleting && setBlogToDelete(null)}
              className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-sm rounded-3xl border border-slate-100 bg-white p-8 text-center shadow-2xl"
            >
              <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full border-[6px] border-red-50/50 bg-red-50 text-red-500">
                <AlertTriangle size={36} strokeWidth={2.5} />
              </div>
              <h3 className="mb-2 text-2xl font-black text-slate-900">Delete Blog?</h3>
              <p className="mb-8 text-sm font-medium text-slate-500">
                This blog will be removed permanently. This action cannot be undone.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setBlogToDelete(null)}
                  disabled={isDeleting}
                  className="flex-1 rounded-xl bg-slate-100 px-4 py-3 font-bold text-slate-600 transition hover:bg-slate-200"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-red-500 px-4 py-3 font-bold text-white shadow-lg shadow-red-500/30 transition hover:bg-red-600"
                >
                  {isDeleting ? <Loader2 size={18} className="animate-spin" /> : "Yes, Delete"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="mb-7 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-950">Blogs</h1>
          <p className="mt-2 text-sm text-slate-500">Manual and automatically generated SEVAA Foundation stories.</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 shadow-sm">
          Total: {blogs.length}
        </div>
      </div>

      {isLoading ? (
        <div className="flex min-h-[50vh] items-center justify-center">
          <Loader2 className="animate-spin text-orange-600" size={36} />
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center font-semibold text-red-700">
          {error}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {blogs.map((blog) => (
            <article
              key={blog.id}
              className="group relative cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
              onClick={() => navigate(`/n-jio-sevaa/blogs/preview/${blog.id}`)}
            >
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  setBlogToDelete(blog.id);
                }}
                className="absolute right-3 top-3 z-10 rounded-xl bg-white/95 p-2.5 text-red-500 shadow-lg backdrop-blur-sm transition hover:bg-red-500 hover:text-white"
                title="Delete Blog"
              >
                <Trash2 size={16} strokeWidth={2.5} />
              </button>

              <div className="relative h-48 overflow-hidden bg-slate-100">
                <img
                  src={blog.cover_image || defaultImage}
                  alt={`${blog.title} cover`}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
                {blog.generation_source === "ai" && (
                  <span className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-slate-950/85 px-3 py-1.5 text-xs font-black text-white backdrop-blur-sm">
                    <Bot size={13} /> Automatic
                  </span>
                )}
              </div>

              <div className="p-5">
                <div className="mb-3 flex flex-wrap gap-2">
                  {blog.category && (
                    <span className="rounded-full bg-orange-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-orange-700">
                      {blog.category}
                    </span>
                  )}
                  <span
                    className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${
                      blog.status === "draft"
                        ? "bg-amber-50 text-amber-700"
                        : "bg-emerald-50 text-emerald-700"
                    }`}
                  >
                    {blog.status || "published"}
                  </span>
                </div>

                <h2 className="mb-2 line-clamp-2 text-lg font-black leading-6 text-slate-900 transition group-hover:text-orange-600">
                  {blog.title}
                </h2>
                <p className="mb-4 line-clamp-3 text-sm leading-6 text-slate-500">{blog.short_description}</p>
                <div className="flex items-center justify-between border-t border-slate-100 pt-4 text-xs font-semibold text-slate-400">
                  <span>{blog.author_name || "SEVAA Foundation"}</span>
                  <span>
                    {new Date(blog.published_at || blog.created_at || Date.now()).toLocaleDateString("en-IN")}
                  </span>
                </div>
              </div>
            </article>
          ))}

          {blogs.length === 0 && (
            <div className="col-span-full rounded-2xl border border-slate-200 bg-white py-20 text-center text-slate-500">
              No blogs found.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BlogList;
